import { useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { useRecoilValue, translationDataState, tourConfigsState } from '../../../store';
import TreedisPublicDAO from '@/api/TreedisPublicDAO';

const DiscountMainWrap = styled.div<{ rtl?: boolean }>`
  &.bl-ticket-order-discount {
    margin-top: 13px;
    cursor: pointer;

    .bl-ticket-order-discount-text {
      .fa-ticket-alt {
        margin-right: 5px;
      }

      .fa-chevron-down {
        margin-left: 5px;
      }
    }

    .bl-ticket-order-discount-input-activate {
      display: flex;
      height: 35px;
      position: relative;

      .bl-discount-input-wrap {
        display: flex;
        position: relative;
        width: 100%;

        .bl-discount-error-icon {
          background: #ea475c;
          width: 14px;
          height: 14px;
          position: absolute;
          right: 10px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          top: 10px;
          font-weight: 300;
          font-size: 12px;
          ${({ rtl }) => `${rtl ? 'right: initial;position: absolute;left: 8px;' : ''}`};
        }

        .bl-applied-discount-value {
          position: absolute;
          font-weight: 300;
          font-size: 15px;
          line-height: 21px;
          display: flex;
          align-items: center;
          top: 8px;
          height: 18px;
          right: 12px;

          ${({ rtl }) => `${rtl ? 'right: initial;left: 15px;' : ''}`};

          .bl-confirmed-icon-wrap {
            font-weight: 300;
            line-height: 21px;
            width: 14px;
            height: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #59df76;
            border-radius: 50%;
            margin-left: 6px;
            color: white;
            font-size: 8px;
          }
        }
      }

      i.fa-ticket-alt {
        z-index: 1;
        position: absolute;
        top: 10px;
        left: 8px;
        ${({ rtl }) => `${rtl ? 'margin: 0 9px;left: initial;right:8px;' : ''}`};
      }

      input {
        padding-left: 25px;
        height: 35px;
        background: #ffffff;
        /* Dark/Dark 1 */

        border: 1px solid #b2bcca;
        box-sizing: border-box;
        border-radius: 8px;
        ${({ rtl }) => `${rtl ? 'padding: 8px 30px;' : ''}`};

        &:active,
        &:focus {
          outline: 2px solid #b2bcca;
        }

        &.error {
          border-color: #ea475c;

          &:active,
          &:focus {
            outline: 2px solid #ea475c;
          }
        }
      }

      button {
        margin-left: 18px;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        color: #ea475c;
        background: transparent;
        border: 2px solid #ea475c;
        box-sizing: border-box;
        border-radius: 12px;
        padding: 0px 35px;
        ${({ rtl }) => `${rtl ? 'margin-right: 18px;margin-left: 0;' : ''}`};

        &:active {
          background: rgba(234, 71, 92, 0.1);
        }
      }

      &.applied {
        button {
          color: white;
          border: none;
          background-color: #21d349bf;

          &:active {
            opacity: 0.9;
          }
        }
      }
    }

    .bl-discount-error-message {
      font-weight: 400;
      font-size: 12px;
      line-height: 20px;
      color: #ea475c;
      margin-top: 4px;
      ${({ rtl }) => `${rtl ? 'text-align: right;direction: rtl;' : ''}`};
    }
  }
`;

const DiscountField = (props: {
  tourId: number;
  ticketId: number;
  ticketingType: 'ticketingToursByYourself' | 'ticketingGameTour' | 'ticketingGuidedTour';
  currencySign: string;
  onDiscountApply: (discount: any) => void;
}) => {
  const { tourId, ticketId, ticketingType, currencySign, onDiscountApply } = props;
  const [isAbleToSetDiscount, setIsAbleToSetDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<any>({});
  const {
    add_a_gift_card_or_discount_code = 'Add a gift card or discount code',
    applyTicket = 'Apply',
    confirmed = 'Confirmed',
    promocode_not_found = 'promocode not found',
  } = useRecoilValue(translationDataState);

  const loadPromocode = async () => {
    try {
      if (!appliedDiscount?.amount && tourId) {
        setDiscountLoading(true);
        const promocodeRes = await TreedisPublicDAO.getTicketingDiscount({
          query: {
            tourId,
            codeName: discountCode,
            ticketId,
            ticketingType,
          },
        });
        if (promocodeRes && promocodeRes.message.includes('Success')) {
          setAppliedDiscount(promocodeRes.data);
          onDiscountApply(promocodeRes.data);
        } else {
          setAppliedDiscount({ error: promocode_not_found });
        }
      }
    } catch (err) {
      setAppliedDiscount({ error: err.message });
    } finally {
      setDiscountLoading(false);
    }
  };
  const { rtl } = useRecoilValue(tourConfigsState);

  return (
    <DiscountMainWrap className="bl-ticket-order-discount" rtl={rtl}>
      {!isAbleToSetDiscount && (
        <div className="bl-ticket-order-discount-text" onClick={() => setIsAbleToSetDiscount(true)}>
          <i className="far fa-ticket-alt" /> {add_a_gift_card_or_discount_code}{' '}
          <i className="far fa-chevron-down" />
        </div>
      )}
      {isAbleToSetDiscount && (
        <div
          className={classNames(
            'bl-ticket-order-discount-input-activate',
            appliedDiscount.amount && 'applied',
          )}
        >
          <i className="far fa-ticket-alt" />
          <div className="bl-discount-input-wrap">
            {appliedDiscount.amount && (
              <div className="bl-applied-discount-value">
                -{appliedDiscount.amount}
                {appliedDiscount.discountType === 'percent' ? '%' : currencySign}
                <div className="bl-confirmed-icon-wrap">
                  <i className="fal fa-check" />
                </div>
              </div>
            )}
            {appliedDiscount.error && (
              <div className="bl-discount-error-icon">
                <i className="fal fa-times" />
              </div>
            )}
            <input
              className={classNames(appliedDiscount.error && 'error')}
              disabled={discountLoading || appliedDiscount.amount}
              type="text"
              value={discountCode}
              onChange={(ev: any) => setDiscountCode(ev.target.value)}
            />
          </div>
          <button onClick={loadPromocode}>
            {appliedDiscount.amount ? confirmed : applyTicket}
          </button>
        </div>
      )}
      {appliedDiscount.error && (
        <div className="bl-discount-error-message">*{appliedDiscount.error}</div>
      )}
    </DiscountMainWrap>
  );
};

export default DiscountField;
